import type { EfficiencyProposalDto, EfficiencyValueDto, PlantProposalsDto, PlantSiteDto, ProposalAnswerInput, ValueOrigin } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../../lib/api';
import { APP_NAME } from '../../../lib/app-name';
import { useTenant, useTenantQuery } from '../../../lib/useTenant';
import { ErrorText } from '../../../ui';
import './proposals.css';

/**
 * Dove il prodotto fa risparmiare tempo (ADR 0138): le proposte di ogni reparto nella pianta viva, con il conto fatto dal
 * server, l'origine di ogni numero e le domande per i numeri che solo il titolare sa.
 */

const ORIGIN: Record<ValueOrigin, { label: string; title: string }> = {
  azienda: { label: 'A', title: 'dato dell’azienda' },
  progetto: { label: 'P', title: 'dall’organigramma ideale' },
  libreria: { label: 'L', title: 'dalla libreria' },
  titolare: { label: 'T', title: 'detto da te' },
  misura: { label: 'M', title: `misurato da ${APP_NAME}` },
  ipotesi: { label: '?', title: 'ipotesi da confermare' },
};
const ROUTE = { tabella: 'tabella del Ministero del Lavoro', minimi: 'minimi del contratto (CNEL), stima', bilancio: 'bilancio dell’azienda', istat: 'media di settore ISTAT', nessuno: 'nessuna fonte' } as const;
const eur = (n: number) => n.toLocaleString('it-IT', { maximumFractionDigits: 0 });
const eur2 = (n: number) => n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const hrs = (n: number) => n.toLocaleString('it-IT', { maximumFractionDigits: 1 });

export function useProposals() {
  return useTenantQuery<PlantProposalsDto>(['plant-proposals'], '/onboarding/plant/proposals', { refetchInterval: (d) => (d?.sedi.some((s) => s.generating) ? 5000 : false) });
}

function useProposalActions() {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const key = ['tenant', tenant.id, 'plant-proposals'];
  const set = (d: PlantProposalsDto) => qc.setQueryData(key, d);
  const answer = useMutation({
    mutationFn: ({ id, decisione }: { id: string; decisione: ProposalAnswerInput['decisione'] }) => api<PlantProposalsDto>(t(`/onboarding/plant/proposals/${id}/answer`), { method: 'POST', body: { decisione } }),
    onSuccess: set,
  });
  const value = useMutation({
    mutationFn: ({ id, domanda, valore }: { id: string; domanda: string; valore: number }) => api<PlantProposalsDto>(t(`/onboarding/plant/proposals/${id}/value`), { method: 'POST', body: { domanda, valore } }),
    onSuccess: set,
  });
  return { answer, value };
}

/** In testa alla sede: quanto tempo libera il prodotto in tutta la sede. */
export function SiteSavings({ site }: { site: PlantSiteDto }) {
  const q = useProposals();
  const s = q.data?.sedi.find((x) => x.sede_id === site.id);
  if (!s) return null;
  if (s.generating)
    return (
      <div className="fxp-scope">
        <div className="fxp-save">
          <span className="fxp-save-load" />
          <p>Sto calcolando, reparto per reparto, dove {APP_NAME} ti fa risparmiare tempo.</p>
        </div>
      </div>
    );
  if (!s.ore_mese) return s.errore ? <p className="fxp-note">Le proposte di questa sede non sono riuscite: riprovo quando cambia qualcosa.</p> : null;
  return (
    <div className="fxp-scope">
    <div className="fxp-save">
      <div>
        <b>{hrs(s.ore_mese)} ore al mese</b>
        {s.euro_anno != null && <b className="eur">{eur(s.euro_anno)} € {s.mesi && s.mesi < 12 ? 'a stagione' : 'l’anno'}</b>}
      </div>
      <p>Il tempo che {APP_NAME} libera in questa sede, sommato su tutti i reparti. Tocca un reparto per vedere le proposte e il conto.</p>
    </div>
    </div>
  );
}

/** Nel pannello del reparto: le sue proposte, e ciò che resta senza proposta con il perché. */
export function RoomProposals({ siteId, roomId }: { siteId: string; roomId: string }) {
  const q = useProposals();
  const s = q.data?.sedi.find((x) => x.sede_id === siteId);
  const list = q.data?.proposte.filter((p) => p.sede_id === siteId && p.reparto_id === roomId) ?? [];
  const out = q.data?.escluse.filter((e) => e.sede_id === siteId && e.reparto_id === roomId) ?? [];
  if (q.error) return <ErrorText>{errorMessage(q.error)}</ErrorText>;
  return (
    <div className="fxp-scope">
      <h4>Dove {APP_NAME} ti fa risparmiare tempo</h4>
      {!q.data || s?.generating ? (
        <div className="fxp-save">
          <span className="fxp-save-load" />
          <p>Sto scrivendo le proposte di questo reparto con il conto.</p>
        </div>
      ) : list.length ? (
        <ul className="fxp-props">
          {list.map((p) => (
            <ProposalCard key={p.id} p={p} />
          ))}
        </ul>
      ) : (
        <p>{s?.errore ? 'Le proposte non sono riuscite: riprovo quando cambia qualcosa nella sede.' : 'Nessuna proposta per questo reparto.'}</p>
      )}
      {!!out.length && !s?.generating && (
        <p className="fxp-note">
          Senza proposta: {out.map((e) => `${e.cosa} (${e.perche})`).join('; ')}.
        </p>
      )}
    </div>
  );
}

function Origin({ v }: { v: EfficiencyValueDto }) {
  const o = ORIGIN[v.origine];
  return (
    <span className={`fxp-o o-${v.origine}`} title={`${o.title}${v.perche ? `: ${v.perche}` : ''}`}>
      {o.label}
    </span>
  );
}

function ProposalCard({ p }: { p: EfficiencyProposalDto }) {
  const [open, setOpen] = useState(false);
  const { answer, value } = useProposalActions();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const costs = [...new Map(p.righe.map((r) => [r.costo.formula, r])).values()];
  const year = p.mesi.valore < 12 ? 'a stagione' : 'l’anno';
  return (
    <li className={`fxp-prop${p.stato !== 'aperta' ? ' done' : ''}`}>
      <button className="fxp-prop-h" aria-expanded={open} onClick={() => setOpen((o) => !o)}>
        <span>
          {p.titolo}
          <small>
            {p.stato === 'accettata' ? 'attivata: è tra i processi da avviare' : p.stato === 'rimandata' ? `rimandata${p.ripresa ? ` al ${new Date(p.ripresa).toLocaleDateString('it-IT')}` : ''}` : p.stima === 'da_confermare' ? 'stima da confermare' : p.stima === 'misurata' ? 'misurata' : 'stimata'}
          </small>
        </span>
        <span className="v">
          {hrs(p.ore_mese)} h/mese
          {p.euro_anno != null && <b>{eur(p.euro_anno)} € {year}</b>}
        </span>
      </button>
      {open && (
        <div className="fxp-prop-b">
          <div>
            <h5>Il problema</h5>
            <p>{p.problema}</p>
          </div>
          <div>
            <h5>Cosa fa {APP_NAME}</h5>
            <p>{p.soluzione.cosa}</p>
            {p.soluzione.dati && <p><em>Dati:</em> {p.soluzione.dati}</p>}
            {p.soluzione.automatismi && <p><em>Da solo:</em> {p.soluzione.automatismi}</p>}
            {!!p.collegamenti.length && (
              <p>
                <em>Programmi:</em>{' '}
                {p.collegamenti
                  .map((c) => `${c.programma} — ${c.modo === 'collegamento' ? (c.verificato ? 'collegato' : 'file esportato, o collegamento diretto se il programma lo permette') : c.modo === 'file' ? 'file esportato' : 'a mano'}${c.cosa ? ` (${c.cosa})` : ''}`)
                  .join('; ')}
              </p>
            )}
            <div className="fxp-tags">
              {p.capacita.map((c) => (
                <span key={c.chiave}>{c.nome}</span>
              ))}
            </div>
          </div>
          <div className="fxp-calc">
            <table>
              <thead>
                <tr>
                  <th>Ruolo e lavoro</th>
                  <th>Volte/mese</th>
                  <th>Minuti</th>
                  <th>Persone</th>
                  <th>Ore/mese</th>
                </tr>
              </thead>
              <tbody>
                {p.righe.map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.ruolo}
                      <small>{r.lavoro}</small>
                    </td>
                    <td className="n">
                      {hrs(r.volte_mese.valore)}
                      <Origin v={r.volte_mese} />
                    </td>
                    <td className="n">
                      {hrs(r.minuti_prima.valore)} → {hrs(r.minuti_dopo.valore)}
                      <Origin v={r.minuti_prima} />
                    </td>
                    <td className="n">
                      {r.persone.valore}
                      <Origin v={r.persone} />
                    </td>
                    <td className="n">{hrs(r.ore_mese)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="fxp-formula">
            ore = volte × minuti risparmiati × persone ÷ 60 · {hrs(p.ore_mese)} h/mese
            {p.euro_mese != null && (
              <>
                {' '}
                = {eur2(p.euro_mese)} €/mese × {p.mesi.valore} mesi = <b>{eur(p.euro_anno ?? 0)} €</b>
                {!p.euro_completo && ' (solo le righe con un costo orario)'}
              </>
            )}
          </p>
          <div>
            <h5>Il costo orario</h5>
            {costs.map((r) => (
              <p key={r.id} className="fxp-cost">
                <em>{ROUTE[r.costo.strada]}:</em> {r.costo.formula}
                {r.costo.fonte && <small>Fonte: {r.costo.fonte}</small>}
                {r.costo.avvertenza && <small>{r.costo.avvertenza}</small>}
              </p>
            ))}
            <small className="fxp-dimtxt">È il valore del tempo liberato: diventa risparmio di cassa quando evita ore pagate (straordinari, stagionali, lavoro esterno).</small>
          </div>
          {(!!p.efficienza.length || !!p.obblighi.length) && (
            <div>
              <h5>Cosa migliora</h5>
              <ul>
                {p.efficienza.map((x) => (
                  <li key={x}>{x}</li>
                ))}
                {p.obblighi.map((o) => (
                  <li key={o.testo}>
                    {o.testo}
                    {o.norma && ` (${o.norma}${o.verificato ? ', letta dalla fonte ufficiale' : ', da verificare'})`}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <div>
            <h5>Termini</h5>
            <ul>
              <li><em>Serve:</em> {p.termini.serve}</li>
              <li><em>Avvio:</em> {p.termini.avvio}</li>
              <li><em>Prima deve esserci:</em> {p.termini.prerequisiti}</li>
              <li><em>Resta alle persone:</em> {p.termini.a_carico}</li>
              <li><em>Limiti e rischi:</em> {p.termini.limiti}</li>
            </ul>
          </div>
          {!!p.domande.length && (
            <div>
              <h5>Da confermare</h5>
              {p.domande.map((d) => (
                <form
                  key={d.id}
                  className="fxp-q"
                  onSubmit={(e) => {
                    e.preventDefault();
                    const n = Number((draft[d.id] ?? '').replace(',', '.'));
                    if (Number.isFinite(n) && n >= 0) value.mutate({ id: p.id, domanda: d.id, valore: n });
                  }}
                >
                  <label htmlFor={`q-${p.id}-${d.id}`}>{d.domanda}</label>
                  <span>
                    <input id={`q-${p.id}-${d.id}`} inputMode="decimal" value={draft[d.id] ?? ''} onChange={(e) => setDraft((x) => ({ ...x, [d.id]: e.target.value }))} />
                    <button disabled={value.isPending || !(draft[d.id] ?? '').trim()}>Conferma</button>
                  </span>
                </form>
              ))}
              {value.error && <ErrorText>{errorMessage(value.error)}</ErrorText>}
            </div>
          )}
          {(p.stato === 'aperta' || p.stato === 'rimandata') && (
            <span className="fxp-answer">
              <button disabled={answer.isPending} onClick={() => answer.mutate({ id: p.id, decisione: 'accettata' })}>
                Sì, attiviamola
              </button>
              <button disabled={answer.isPending} onClick={() => answer.mutate({ id: p.id, decisione: 'rifiutata' })}>
                No
              </button>
              {p.stato === 'aperta' && (
                <button disabled={answer.isPending} onClick={() => answer.mutate({ id: p.id, decisione: 'rimandata' })}>
                  Più avanti
                </button>
              )}
            </span>
          )}
          {answer.error && <ErrorText>{errorMessage(answer.error)}</ErrorText>}
        </div>
      )}
    </li>
  );
}
